// ลงเวลา (clock-in) helpers — shared by the HR clock-in report + its editor.
// Backend: Xano employee_timetable rows, proxied at /api/hr/clockin*.
//
// Shape reminder: one row = one SCHEDULED SHIFT. The punch lives in the
// embedded `clockin{}` object and may be absent (= ขาดลงเวลา). Xano computes
// the payroll numbers on save:
//   clockin.clockin_price   สาย — NEGATIVE MINUTES late (0 for Office rows)
//   clockin.clockout_price  OT (+) / ออกก่อน (−); baht for a real OT block,
//                           minutes for the ≤1h grace band — Xano's own rule,
//                           so we surface the number and never restate a unit
//   clockin.transport_price copied from the row's travel_fee_amount
import { isLeaveType } from './timetable.js'
import { parseDate as parseDate_ } from './asset.js'

export { parseDate, toDateInput, fmtDateTH, fmtMoney, relabelBranch } from './asset.js'
// Row types + leave classification are shared with the Leave tab — single source.
export { TIMETABLE_TYPES } from './timetable.js'

export const isLeaveRow = (r) => isLeaveType(String(r?.type || ''))

/* ── timestamps ──────────────────────────────────────────────────────────
 * Xano timestamp columns are epoch MILLISECONDS. The clinic and the browser
 * both sit in Asia/Bangkok, so local getters are the right conversion for
 * <input type="datetime-local"> — no manual offset arithmetic. */
const pad = (n) => String(n).padStart(2, '0')

// "HH:mm" for <input type="time">. The DATE half is never editable: a punch
// belongs to its shift, so the day comes from employee_timetable.date and only
// the clock face is the operator's to set.
export function tsToTimeInput(ts) {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return ''
  const d = new Date(n)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Anchor a typed "HH:mm" onto the shift row's own date.
export function timeOnShiftDate(row, hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim())
  if (!m) return null
  const d = parseDate_(row?.date)   // date-only → local midnight
  if (!d) return null
  d.setHours(Number(m[1]), Number(m[2]), 0, 0)
  return d.getTime()
}

const ymd = (t) => { const d = new Date(t); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

// Does the SCHEDULE itself cross midnight? Only then may a clock-out earlier on
// the clock than the clock-in roll to the next day — inferred from the roster,
// never guessed, so a plain typo stays an error instead of silently becoming
// a 20-hour shift.
export function isOvernightShift(r) {
  const a = shiftStart(r); const b = shiftEnd(r)
  return a != null && b != null && ymd(a) !== ymd(b)
}

// True when a stored punch sits on a different calendar day than its shift —
// saving re-anchors it, so the drawer warns first.
export function punchDateDrifts(r) {
  const t = clockinTs(r)
  return t != null && !!r?.date && ymd(t) !== String(r.date).slice(0, 10)
}

export function tsToLocalInput(ts) {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return ''
  const d = new Date(n)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function localInputToTs(s) {
  if (!s) return null
  const t = new Date(s).getTime()
  return Number.isFinite(t) ? t : null
}

// dd/mm/yyyy, ค.ศ. (Gregorian). The rest of the app prints Thai Buddhist dates
// (fmtDateTH); the clock-in report is read against payroll periods and rosters
// where the numeric ค.ศ. form is what people compare against, so it uses this.
export function fmtDate(v) {
  const d = parseDate_(v)
  if (!d) return '—'
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

// Same, with the clock face — for log/remark timestamps.
export function fmtDateTime(v) {
  const d = parseDate_(v)
  if (!d) return '—'
  return `${fmtDate(v)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fmtTime(ts) {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const d = new Date(n)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/* ── row accessors ───────────────────────────────────────────────────────── */
const ts = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null }

export const clockinTs   = (r) => ts(r?.clockin?.clockin)
export const clockoutTs  = (r) => ts(r?.clockin?.clockout)
export const hasClockin  = (r) => clockinTs(r) != null
export const shiftStart  = (r) => ts(r?.start_date)
export const shiftEnd    = (r) => ts(r?.end_date)

// สาย in whole minutes. clockin_price is ≤ 0 (0 = on time / type not counted).
export function lateMinutes(r) {
  const p = Number(r?.clockin?.clockin_price || 0)
  return p < 0 ? Math.round(-p) : 0
}

export const otValue    = (r) => Number(r?.clockin?.clockout_price || 0)

/* OT is one three-state fact, not two independent flags. `clockin_calculation`
 * ZEROES clockout_price whenever special_OT is set, so "both true" silently
 * means special — reading them as two booleans would show a row as earning
 * normal OT when Xano has already decided it earns none. Special wins here too. */
export function otMode(r) {
  if (r?.special_OT) return 'special'
  if (r?.allow_OT) return 'allow'
  return 'none'
}
export const travelFee  = (r) => Number(r?.travel_fee_amount || 0)

// Minutes actually worked (clock-out − clock-in), or null while still on shift.
export function workedMinutes(r) {
  const a = clockinTs(r); const b = clockoutTs(r)
  if (a == null || b == null || b <= a) return null
  return Math.round((b - a) / 60000)
}

export function fmtDuration(mins) {
  if (mins == null) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h ? `${h} ชม. ${m ? `${m} น.` : ''}`.trim() : `${m} น.`
}

/* ── row status pill ─────────────────────────────────────────────────────── */
export function rowStatus(r) {
  if (isLeaveRow(r)) return { label: r.type, tone: 'bg-violet-50 text-violet-700 ring-violet-200' }
  if (!hasClockin(r)) return { label: 'ขาดลงเวลา', tone: 'bg-amber-50 text-amber-700 ring-amber-200' }
  if (clockoutTs(r) == null) return { label: 'ยังไม่ออกงาน', tone: 'bg-sky-50 text-sky-700 ring-sky-200' }
  if (lateMinutes(r) > 0) return { label: 'สาย', tone: 'bg-rose-50 text-rose-700 ring-rose-200' }
  return { label: 'ปกติ', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' }
}

/* ── payroll period ──────────────────────────────────────────────────────
 * The salary run covers the 26th of the previous month → the 25th of this one
 * (same default as gen2), so the clock-in report opens on the period the next
 * payslip will be built from. */
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

export function payrollPeriod(now = new Date()) {
  const anchor = now.getDate() >= 26 ? 1 : 0 // past the 25th → the period that just opened
  const end = new Date(now.getFullYear(), now.getMonth() + anchor, 25)
  const start = new Date(end.getFullYear(), end.getMonth() - 1, 26)
  return { start: iso(start), end: iso(end) }
}

// Step the 26→25 window by whole periods (−1 = previous month's run).
export function shiftPeriod(period, delta) {
  const e = parseISO(period.end)
  const end = new Date(e.getFullYear(), e.getMonth() + delta, 25)
  const start = new Date(end.getFullYear(), end.getMonth() - 1, 26)
  return { start: iso(start), end: iso(end) }
}

function parseISO(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date()
}

/* ── page summary ────────────────────────────────────────────────────────
 * Deliberately computed from the rows on screen (not the stat_* addons): the
 * addons use an EXCLUSIVE end date, so they silently drop the last day of the
 * range. Counting what is displayed is both correct and self-explanatory. */
export function summarize(rows) {
  const list = Array.isArray(rows) ? rows : []
  const shifts = list.filter((r) => !isLeaveRow(r))
  return {
    shifts: shifts.length,
    leave: list.length - shifts.length,
    punched: shifts.filter(hasClockin).length,
    missing: shifts.filter((r) => !hasClockin(r)).length,
    lateCount: shifts.filter((r) => lateMinutes(r) > 0).length,
    lateMins: shifts.reduce((s, r) => s + lateMinutes(r), 0),
    ot: shifts.reduce((s, r) => s + otValue(r), 0),
    travel: list.reduce((s, r) => s + travelFee(r), 0),
  }
}

/* ── server-side sort (Xano external sorting) ────────────────────────────
 * Sorting client-side can only order the rows a page happened to contain, so
 * grouping by employee split anyone whose month straddled a page boundary.
 * Sorting on the SERVER by รหัสพนักงาน puts every one of a person's rows next
 * to each other in the result set, so a page break falls between two people.
 * `Employee` is the endpoint's join alias; `employee_timetable` the base table.
 * Consumed by `override_sort` on Xano's GET /employee_timetable.
 */
export function sortSpec(groupBy) {
  const byCode = { sortBy: 'Employee.code', orderBy: 'asc' }
  const byDate = { sortBy: 'employee_timetable.date', orderBy: 'desc' }
  const levels = groupBy === 'date' ? [byDate, byCode] : [byCode, byDate]
  return levels.map((l) => JSON.stringify(l))
}

/* ── summary rows (GET /api/hr/clockin-summary) ──────────────────────────
 * Postgres returns numeric aggregates as STRINGS, and `late_min` in Xano's own
 * สาย convention: NEGATIVE minutes. Both are normalised once, here, so no view
 * has to remember either quirk. */
export function normSummary(r) {
  const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0 }
  const shifts = n(r?.shifts)
  const punched = n(r?.punched)
  return {
    key: String(r?.key ?? ''),
    employee_id: n(r?.employee_id),
    code: r?.code || '',
    name: r?.name || '',
    nickname: r?.nickname || '',
    date: r?.date || '',
    days: n(r?.days),
    people: n(r?.people),
    shifts,
    leave: n(r?.leave_days),
    punched,
    missing: Math.max(0, shifts - punched),
    lateCount: n(r?.late_n),
    lateMins: Math.round(Math.abs(n(r?.late_min))), // stored negative
    ot: n(r?.ot),
    travel: n(r?.travel),
  }
}

// Column totals for the summary table's footer row.
export function totalSummary(rows) {
  const list = Array.isArray(rows) ? rows : []
  const add = (k) => list.reduce((s, r) => s + (r[k] || 0), 0)
  return {
    groups: list.length,
    shifts: add('shifts'), punched: add('punched'), missing: add('missing'),
    leave: add('leave'), lateCount: add('lateCount'), lateMins: add('lateMins'),
    ot: add('ot'), travel: add('travel'),
  }
}

/* ── shift-type colour ───────────────────────────────────────────────────
 * A roster is scanned, not read: one glance should say "this person is on
 * mornings" without parsing eight Thai words. Hues are chosen to be distinct
 * from each other AND from the status pills (emerald ปกติ / amber ขาดลงเวลา /
 * rose สาย), so a coloured type can never be mistaken for a verdict.
 * The leave tones match `leaveTypeMeta()` in the shared timetable.js, so a
 * ลาป่วย row looks the same here as it does on the การลา tab. */
const TYPE_TONES = {
  'กะเช้า':     'bg-sky-50 text-sky-700 ring-sky-200',
  'กะบ่าย':     'bg-indigo-50 text-indigo-700 ring-indigo-200',
  'กะควบ':      'bg-purple-50 text-purple-700 ring-purple-200',
  'เวรผู้ช่วย':  'bg-teal-50 text-teal-700 ring-teal-200',
  'WFH':        'bg-cyan-50 text-cyan-700 ring-cyan-200',
  'Office':     'bg-slate-100 text-slate-600 ring-slate-200',
  'ลากิจ':      'bg-amber-50 text-amber-700 ring-amber-200',
  'ลาป่วย':     'bg-rose-50 text-rose-700 ring-rose-200',
  'Leave without pay': 'bg-stone-200 text-stone-700 ring-stone-300',
}
export const shiftTypeTone = (type) => TYPE_TONES[String(type || '')] || 'bg-slate-100 text-slate-500 ring-slate-200'

/* ── date spine ──────────────────────────────────────────────────────────
 * A person's detail lists EVERY day of the period, newest first — not just the
 * days that have a row. A day with no employee_timetable record is a real
 * finding ("this person was never rostered") and is invisible if the table only
 * renders what the query returned. */
export function dateSpineDesc(startISO, endISO) {
  const a = parseDate_(startISO)
  const b = parseDate_(endISO)
  if (!a || !b || b < a) return []
  const out = []
  const d = new Date(b.getFullYear(), b.getMonth(), b.getDate())
  const stop = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime()
  // Bounded so a mis-typed range can never spin: a payroll period is ~31 days.
  while (d.getTime() >= stop && out.length < 400) {
    out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
    d.setDate(d.getDate() - 1)
  }
  return out
}

/* ── range guard ─────────────────────────────────────────────────────────
 * A payroll period is ~31 days and the detail view renders one row per day, so
 * that is the working ceiling. Enforced by CLAMPING rather than rejecting: the
 * user edited one end deliberately, so that end is honoured and the OTHER is
 * pulled in to fit — an error toast would just make them do the arithmetic. */
export const MAX_RANGE_DAYS = 31
const DAY_MS = 86400000

export function clampPeriod(period, edited) {
  const a = parseDate_(period.start)
  const b = parseDate_(period.end)
  if (!a || !b) return period
  let start = a
  let end = b
  // An inverted range collapses onto the end the user just set.
  if (end < start) {
    if (edited === 'start') end = new Date(start)
    else start = new Date(end)
  }
  if (Math.round((end - start) / DAY_MS) + 1 > MAX_RANGE_DAYS) {
    if (edited === 'start') end = new Date(start.getTime() + (MAX_RANGE_DAYS - 1) * DAY_MS)
    else start = new Date(end.getTime() - (MAX_RANGE_DAYS - 1) * DAY_MS)
  }
  return { start: iso(start), end: iso(end) }
}

// Bounds for the <input type="date"> pair, so the browser blocks most of it
// before clampPeriod ever has to correct anything.
export function periodBounds(period) {
  const a = parseDate_(period.start)
  const b = parseDate_(period.end)
  const span = (MAX_RANGE_DAYS - 1) * DAY_MS
  return {
    startMin: b ? iso(new Date(b.getTime() - span)) : '',
    startMax: b ? iso(b) : '',
    endMin: a ? iso(a) : '',
    endMax: a ? iso(new Date(a.getTime() + span)) : '',
  }
}
